import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, ClipboardList, Filter, Plus, RefreshCw, Save, Search, X } from 'lucide-react';
import Header from '../layout/Header';

type TicketStatus = 'Nouveau' | 'À analyser' | 'En cours' | 'Bloqué' | 'En validation' | 'Terminé' | 'Annulé';
type TicketPriority = 'Basse' | 'Normale' | 'Haute' | 'Urgente';

type Ticket = {
  id: string;
  ticketNo: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: string;
  source: string;
  requesterName: string;
  requesterEmail: string;
  assignedTo: string[];
  dueDate: string;
  nextAction: string;
  internalNotes: string;
  createdAt: string;
  updatedAt: string;
  commentCount: number;
  attachmentCount: number;
};

type Meta = {
  committeeMembers: string[];
  statuses: TicketStatus[];
  priorities: TicketPriority[];
  categories: string[];
};

type Stats = {
  total: number;
  open: number;
  blocked: number;
  high_priority: number;
  overdue: number;
};

const emptyTicket = (): Partial<Ticket> => ({
  title: '',
  description: '',
  status: 'Nouveau',
  priority: 'Normale',
  category: 'Autre',
  source: 'Comité Agentique',
  requesterName: '',
  requesterEmail: '',
  assignedTo: [],
  dueDate: '',
  nextAction: '',
  internalNotes: '',
});

const statusClass: Record<TicketStatus, string> = {
  Nouveau: 'bg-slate-100 text-slate-700 border-slate-200',
  'À analyser': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'En cours': 'bg-blue-50 text-blue-700 border-blue-200',
  Bloqué: 'bg-red-50 text-red-700 border-red-200',
  'En validation': 'bg-violet-50 text-violet-700 border-violet-200',
  Terminé: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Annulé: 'bg-gray-100 text-gray-500 border-gray-200',
};

const priorityClass: Record<TicketPriority, string> = {
  Basse: 'text-slate-500',
  Normale: 'text-blue-700',
  Haute: 'text-orange-700 font-semibold',
  Urgente: 'text-red-700 font-bold',
};

function compactDate(value?: string) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('fr-CA', { month: '2-digit', day: '2-digit' });
}

function daysLate(value?: string) {
  if (!value) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${value}T00:00:00`);
  return due < today;
}

export default function AgentiqueTaches() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [meta, setMeta] = useState<Meta>({ committeeMembers: [], statuses: [], priorities: [], categories: [] });
  const [stats, setStats] = useState<Stats>({ total: 0, open: 0, blocked: 0, high_priority: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Partial<Ticket>>(emptyTicket());
  const [filters, setFilters] = useState({ q: '', status: '', priority: '', assignee: '', openOnly: '1' });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return params.toString();
  }, [filters]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/prisme/bz-agentique/tickets?${queryString}`, { credentials: 'include' });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Impossible de charger les tâches');
      setTickets(payload.tickets || []);
      setMeta(payload.meta || { committeeMembers: [], statuses: [], priorities: [], categories: [] });
      setStats(payload.stats || { total: 0, open: 0, blocked: 0, high_priority: 0, overdue: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  function startCreate() {
    setEditingTicket(emptyTicket());
    setShowForm(true);
  }

  function startEdit(ticket: Ticket) {
    setEditingTicket(ticket);
    setShowForm(true);
  }

  function toggleAssignee(name: string) {
    const current = editingTicket.assignedTo || [];
    const assignedTo = current.includes(name) ? current.filter((item) => item !== name) : [...current, name];
    setEditingTicket({ ...editingTicket, assignedTo });
  }

  async function saveTicket() {
    if (!editingTicket.title?.trim()) {
      setError('Titre requis.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const isEdit = Boolean(editingTicket.id);
      const response = await fetch(`/api/prisme/bz-agentique/tickets${isEdit ? `/${editingTicket.id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingTicket),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Sauvegarde impossible');
      setShowForm(false);
      setEditingTicket(emptyTicket());
      await loadTickets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Header
        title="Gestion demandes comité — Tâches"
        subtitle="Listing compact des demandes internes BZ liées à l’implantation des bots. Simple, clavier-friendly, zéro kanban."
      />

      <main className="mx-auto max-w-7xl px-6 pb-12">
        <section className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-5">
          {[
            { label: 'Ouvertes', value: stats.open, tone: 'bg-blue-600', icon: ClipboardList },
            { label: 'Haute/Urgente', value: stats.high_priority, tone: 'bg-orange-500', icon: AlertCircle },
            { label: 'Bloquées', value: stats.blocked, tone: 'bg-red-600', icon: X },
            { label: 'En retard', value: stats.overdue, tone: 'bg-amber-500', icon: AlertCircle },
            { label: 'Total', value: stats.total, tone: 'bg-slate-800', icon: CheckCircle2 },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.label}</p>
                    <p className="mt-1 text-3xl font-black text-slate-900">{card.value}</p>
                  </div>
                  <div className={`rounded-xl ${card.tone} p-2.5 text-white`}><Icon className="h-5 w-5" /></div>
                </div>
              </div>
            );
          })}
        </section>

        <section className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-5">
              <label className="md:col-span-2">
                <span className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-500"><Search className="h-3.5 w-3.5" /> Recherche</span>
                <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Titre, BZAG, action…" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
              </label>
              <label>
                <span className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-500"><Filter className="h-3.5 w-3.5" /> Statut</span>
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
                  <option value="">Tous</option>
                  {meta.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
              <label>
                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Priorité</span>
                <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
                  <option value="">Toutes</option>
                  {meta.priorities.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                </select>
              </label>
              <label>
                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Assigné</span>
                <select value={filters.assignee} onChange={(e) => setFilters({ ...filters, assignee: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
                  <option value="">Tous</option>
                  {meta.committeeMembers.map((member) => <option key={member} value={member}>{member}</option>)}
                </select>
              </label>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setFilters({ ...filters, openOnly: filters.openOnly ? '' : '1' })} className={`rounded-xl px-3 py-2 text-sm font-semibold ${filters.openOnly ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>Ouvertes seulement</button>
              <button type="button" onClick={loadTickets} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><RefreshCw className="inline h-4 w-4" /></button>
              <button type="button" onClick={startCreate} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-blue-700"><Plus className="mr-1 inline h-4 w-4" /> Nouvelle tâche</button>
            </div>
          </div>
        </section>

        {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}

        {showForm && (
          <section className="mb-4 rounded-2xl border border-blue-200 bg-white p-5 shadow-lg shadow-blue-100/60">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-black text-slate-900">{editingTicket.id ? `Modifier ${editingTicket.ticketNo}` : 'Nouvelle tâche comité'}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
              <input value={editingTicket.title || ''} onChange={(e) => setEditingTicket({ ...editingTicket, title: e.target.value })} placeholder="Titre court" className="lg:col-span-5 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <select value={editingTicket.status || 'Nouveau'} onChange={(e) => setEditingTicket({ ...editingTicket, status: e.target.value as TicketStatus })} className="lg:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm">
                {meta.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
              <select value={editingTicket.priority || 'Normale'} onChange={(e) => setEditingTicket({ ...editingTicket, priority: e.target.value as TicketPriority })} className="lg:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm">
                {meta.priorities.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
              </select>
              <select value={editingTicket.category || 'Autre'} onChange={(e) => setEditingTicket({ ...editingTicket, category: e.target.value })} className="lg:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm">
                {meta.categories.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
              <input type="date" value={editingTicket.dueDate || ''} onChange={(e) => setEditingTicket({ ...editingTicket, dueDate: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <textarea value={editingTicket.description || ''} onChange={(e) => setEditingTicket({ ...editingTicket, description: e.target.value })} placeholder="Description / demande" className="lg:col-span-6 min-h-[88px] rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <textarea value={editingTicket.nextAction || ''} onChange={(e) => setEditingTicket({ ...editingTicket, nextAction: e.target.value })} placeholder="Prochaine action concrète" className="lg:col-span-6 min-h-[88px] rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <div className="lg:col-span-12 flex flex-wrap gap-2">
                {meta.committeeMembers.map((member) => (
                  <button key={member} type="button" onClick={() => toggleAssignee(member)} className={`rounded-full border px-3 py-1.5 text-xs font-bold ${editingTicket.assignedTo?.includes(member) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>{member}</button>
                ))}
              </div>
              <textarea value={editingTicket.internalNotes || ''} onChange={(e) => setEditingTicket({ ...editingTicket, internalNotes: e.target.value })} placeholder="Notes internes" className="lg:col-span-12 min-h-[70px] rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-600">Annuler</button>
              <button type="button" disabled={saving} onClick={saveTicket} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-60"><Save className="mr-1 inline h-4 w-4" /> Sauvegarder</button>
            </div>
          </section>
        )}

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-900 text-left text-xs font-black uppercase tracking-wide text-white">
                <tr>
                  <th className="px-3 py-3">Ticket</th>
                  <th className="px-3 py-3">Titre / action</th>
                  <th className="px-3 py-3">Statut</th>
                  <th className="px-3 py-3">Priorité</th>
                  <th className="px-3 py-3">Assigné</th>
                  <th className="px-3 py-3">Échéance</th>
                  <th className="px-3 py-3">Catégorie</th>
                  <th className="px-3 py-3 text-right">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading && <tr><td colSpan={8} className="px-3 py-8 text-center font-semibold text-slate-500">Chargement…</td></tr>}
                {!loading && tickets.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center font-semibold text-slate-500">Aucune tâche dans ce filtre.</td></tr>}
                {!loading && tickets.map((ticket) => (
                  <tr key={ticket.id} onDoubleClick={() => startEdit(ticket)} className="cursor-pointer hover:bg-blue-50/45">
                    <td className="whitespace-nowrap px-3 py-3 font-mono text-xs font-black text-slate-700">{ticket.ticketNo}</td>
                    <td className="min-w-[360px] px-3 py-3">
                      <button type="button" onClick={() => startEdit(ticket)} className="text-left font-bold text-slate-900 hover:text-blue-700">{ticket.title}</button>
                      <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{ticket.nextAction || ticket.description || '—'}</p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3"><span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${statusClass[ticket.status]}`}>{ticket.status}</span></td>
                    <td className={`whitespace-nowrap px-3 py-3 text-xs ${priorityClass[ticket.priority]}`}>{ticket.priority}</td>
                    <td className="min-w-[210px] px-3 py-3 text-xs font-semibold text-slate-700">{ticket.assignedTo.length ? ticket.assignedTo.join(', ') : '—'}</td>
                    <td className={`whitespace-nowrap px-3 py-3 text-xs font-bold ${daysLate(ticket.dueDate) && ticket.status !== 'Terminé' ? 'text-red-700' : 'text-slate-600'}`}>{compactDate(ticket.dueDate)}</td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-slate-600">{ticket.category}</td>
                    <td className="whitespace-nowrap px-3 py-3 text-right text-xs text-slate-500">{ticket.commentCount} com. / {ticket.attachmentCount} doc.</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">Double-clic sur une ligne pour modifier. Format volontairement compact, style Excel, pour FD et Vince.</div>
        </section>
      </main>
    </div>
  );
}
